import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import type { Theme} from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { UserContextProps } from '../../Contexts/UserContext';
import UserContext from '../../Contexts/UserContext';
import type { LocalStorageContextProps } from '../../Contexts/LocalStorageContext';
import LocalStorageContext from '../../Contexts/LocalStorageContext';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { updateLastActivity } from '../../Utils/LastActivity';
import { appBarHeight, navBarHeight } from '../../Constants';
import type { IndexedDBContextProps } from '../../Contexts/IndexedDBContext';
import IndexedDBContext from '../../Contexts/IndexedDBContext';
import MenuItem from '@mui/material/MenuItem';
import FilledChecklist from '../../Models/Checklists/FilledChecklistModel';
import { datetimeStandardParser, datetimeToApiString, datetimeToVisualString } from '../../Utils/DateTimeParser';
import FilledChecklistValueObject from '../../Models/Checklists/FilledChecklistValueObjectModel';
import type ChecklistModel from '../../Models/Checklists/ChecklistModelModel';
import type ChecklistElement from '../../Models/Checklists/Elements/ChecklistElementModel';
import CheckboxChecklistElement from '../../Models/Checklists/Elements/CheckboxChecklistElementModel';
import TextFieldChecklistElement from '../../Models/Checklists/Elements/TextFieldChecklistElementModel';
import CountryChecklistElement from '../../Models/Checklists/Elements/CountryChecklistElementModel';
import type Country from '../../Models/CountryModel';
import MaskChecklistElement from '../../Models/Checklists/Elements/MaskChecklistElementModel';
import TextAreaChecklistElement from '../../Models/Checklists/Elements/TextAreaChecklistElementModel';
import DecimalChecklistElement from '../../Models/Checklists/Elements/DecimalChecklistElementModel';
import IntegerChecklistElement from '../../Models/Checklists/Elements/IntegerChecklistElementModel';
import HeaderChecklistElement from '../../Models/Checklists/Elements/HeaderChecklistElementModel';
import DateChecklistElement from '../../Models/Checklists/Elements/DateChecklistElementModel';
import SimpleChecklistElement from '../../Models/Checklists/Elements/SimpleChecklistElementModel';
import UserChecklistElement from '../../Models/Checklists/Elements/UserChecklistElementModel';
import type User from '../../Models/UserModel';
import DropdownChecklistElement from '../../Models/Checklists/Elements/DropdownChecklistElementModel';
import type InspectingCompany from '../../Models/InspectingCompanyModel';
import type Inspector from '../../Models/InspectorModel';
import type UserDepartmentLink from '../../Models/UserDepartmentLinkModel';
import type Department from '../../Models/DepartmentModel';
import InspectorChecklistElement from '../../Models/Checklists/Elements/InspectorChecklistElementModel';
import InspectingCompanyChecklistElement from '../../Models/Checklists/Elements/InspectingCompanyChecklistElementModel';
import LookupChecklistElement from '../../Models/Checklists/Elements/LookupChecklistElementModel';
import DepartmentChecklistElement from '../../Models/Checklists/Elements/DepartmentChecklistElementModel';
import type Category from '../../Models/CategoryModel';
import CategoryChecklistElement from '../../Models/Checklists/Elements/CategoryChecklistElementModel';
import type DepartmentLink from '../../Models/DepartmentLinkModel';
import { getUniqueId, removeDuplicates, removeNulls } from 'shouldve-been-js';
import { useTitle } from 'shouldve-been-react';
import { ErrorSnackbar, LoadDataBackdrop } from 'shouldve-been-mui';
import BugReport from '../../Models/BugReportModel';

type ChecklistItemSubTabProps = {
    filledChecklistId: string,
    checklistId: string,
    sortIndex: number,
    closeSubTab: (successMessage: string | null) => void,
};

function ChecklistItemSubTab(props: Readonly<ChecklistItemSubTabProps>): JSX.Element {

    useTitle('K-Fleet Experience - Checklist Item');

    const userContext: UserContextProps = useContext<UserContextProps>(UserContext);
    const localStorageContext: LocalStorageContextProps = useContext<LocalStorageContextProps>(LocalStorageContext);
    const indexedDBContext: IndexedDBContextProps = useContext<IndexedDBContextProps>(IndexedDBContext);

    const currentTheme: Theme = useTheme();

    const [windowSize, setWindowSize] = useState<[number, number]>([1920, 1080]);
    const [errorSnackbarMessage, setErrorSnackbarMessage] = useState<string | null>(null);
    const [dataIsLoaded, setDataIsLoaded] = useState<boolean>(false);
    
    const [name, setName] = useState<string>('');
    const [createdDate, setCreatedDate] = useState<Date>(new Date(0));
    const [values, setValues] = useState<FilledChecklistValueObject[]>([]);
    const [checklistModel, setChecklistModel] = useState<ChecklistModel | null>(null);
    const [folderId, setFolderId] = useState<string | null>(null);

    const [countries, setCountries] = useState<Country[]>([]);
    const [allUsers, setAllUsers] = useState<User[]>([]);
    const [inspectors, setInspectors] = useState<Inspector[]>([]);
    const [inspectingCompanies, setInspectingCompanies] = useState<InspectingCompany[]>([]);
    const [departments, setDepartments] = useState<Department[]>([]);
    const [userDepartmentLinks, setUserDepartmentLinks] = useState<UserDepartmentLink[]>([]);
    const [categories, setCategories] = useState<Category[]>([]);

    const handleValueChange: (newValueObject: FilledChecklistValueObject, element: ChecklistElement) => Promise<void> = useCallback(async (newValueObject: FilledChecklistValueObject, element: ChecklistElement) => {

        updateLastActivity(userContext, localStorageContext);

        if (folderId === null) { //should not happen
            await indexedDBContext.indexedDBService.insertBugReport(new BugReport(getUniqueId(), new Date(), 'Checklist Item', 'Required field folderId is null when trying to update checklist field'));
            setErrorSnackbarMessage('An unexpected error occurred');
            return;
        }

        const newChangedDate: Date = new Date();

        let newValues: FilledChecklistValueObject[] = values.filter((valueObject: FilledChecklistValueObject) => valueObject.elementId !== newValueObject.elementId);
        newValues.push(newValueObject);

        //validation
        if (element instanceof DropdownChecklistElement) {

            const elementsWithDependencyToThis: DropdownChecklistElement[] = [];
            
            checklistModel?.elements.forEach((currentElement: ChecklistElement) => {

                if ((currentElement instanceof DropdownChecklistElement) && currentElement.dependentElementId === element.elementId) {
                    elementsWithDependencyToThis.push(currentElement);
                }
            });

            elementsWithDependencyToThis.forEach((dependentElement: DropdownChecklistElement) => {

                const dependentElementValue: FilledChecklistValueObject | null = newValues.find((value: FilledChecklistValueObject) => value.elementId === dependentElement.elementId) ?? null;
                const dependentElementValueIndex: number | null = dependentElement.getValueIndexFromValue(dependentElementValue?.value ?? null);
                const valueIndex: number | null = element.getValueIndexFromValue(newValueObject?.value ?? null);
                
                if (dependentElementValueIndex === null || valueIndex === null || dependentElement.dependencyMapping[dependentElementValueIndex] === undefined || ((dependentElement.dependencyMapping[dependentElementValueIndex]?.length ?? 0) > 0 && !(dependentElement.dependencyMapping[dependentElementValueIndex]?.includes(valueIndex.toString()) ?? false))) {
                    newValues = newValues.filter((currentValueObject: FilledChecklistValueObject) => currentValueObject.elementId !== dependentElement.elementId);
                    newValues.push(new FilledChecklistValueObject(dependentElement.elementId, '', null, null));
                }
            });  
        } else if (element instanceof UserChecklistElement) {

            //lookup
            const lookupElementsWithDependencyToThis: LookupChecklistElement[] = [];
            
            checklistModel?.elements.forEach((currentElement: ChecklistElement) => {

                if ((currentElement instanceof LookupChecklistElement) && currentElement.dependentElementId === element.elementId) {
                    lookupElementsWithDependencyToThis.push(currentElement);
                }
            });

            lookupElementsWithDependencyToThis.forEach((dependentElement: LookupChecklistElement) => {

                if (dependentElement.lookupType === 'CM_USER;USERID;RANK') {

                    const newLookupValue: string | null = allUsers.find((user: User) => user.userId === newValueObject.value)?.rank ?? null;

                    const dependentElementValueObject: FilledChecklistValueObject | null = newValues.find((value: FilledChecklistValueObject) => value.elementId === dependentElement.elementId) ?? null;
                    
                    if (dependentElementValueObject !== null) {
                        newValues = newValues.filter((currentValueObject: FilledChecklistValueObject) => currentValueObject.elementId !== dependentElement.elementId);   
                    }

                    newValues.push(new FilledChecklistValueObject(dependentElement.elementId, newLookupValue, null, null));
                }
            });

            //department
            const departmentElementsWithDependencyToThis: DepartmentChecklistElement[] = [];
            
            checklistModel?.elements.forEach((currentElement: ChecklistElement) => {

                if ((currentElement instanceof DepartmentChecklistElement) && currentElement.dependentElementId === element.elementId) {
                    departmentElementsWithDependencyToThis.push(currentElement);
                }
            });

            departmentElementsWithDependencyToThis.forEach((dependentElement: DepartmentChecklistElement) => {

                if (dependentElement.dependentElementId !== null) {

                    const dependentElementValue: FilledChecklistValueObject | null = newValues.find((value: FilledChecklistValueObject) => value.elementId === dependentElement.elementId) ?? null;
                    const selectedUser: User | null = allUsers.find((user: User) => user.userId === newValueObject.value) ?? null;
                    
                    const currentUserDepartmentLinks: DepartmentLink[] = userDepartmentLinks.find((userDepartmentlink: UserDepartmentLink) => userDepartmentlink.userId === selectedUser?.userId)?.departmentLinks ?? [];
                    const currentRoleDepartmentLinks: DepartmentLink[] = userDepartmentLinks.find((userDepartmentlink: UserDepartmentLink) => userDepartmentlink.userId === selectedUser?.roleId)?.departmentLinks ?? [];
        
                    const filteredUserDepartmentLinks: DepartmentLink[] = currentUserDepartmentLinks.filter((departmentLink: DepartmentLink) => departmentLink.status === '-');
                    const filteredRoleDepartmentLinks: DepartmentLink[] = currentRoleDepartmentLinks.filter((departmentLink: DepartmentLink) => departmentLink.status === '-');
        
                    const userDepartmentIds: string[] = filteredUserDepartmentLinks.map((departmentLink: DepartmentLink) => departmentLink.departmentId);
                    const roleDepartmentIds: string[] = filteredRoleDepartmentLinks.map((departmentLink: DepartmentLink) => departmentLink.departmentId);
        
                    const uniqueDepartmentIds: string[] = removeDuplicates(userDepartmentIds.concat(roleDepartmentIds));
        
                    const currentDepartments: (Department | null)[] = uniqueDepartmentIds.map((departmentId: string) => departments.find((department: Department) => department.departmentId === departmentId) ?? null);
        
                    const filteredDepartments: Department[] = removeNulls(currentDepartments).filter((department: Department) => department.status === '-');
        
                    if (selectedUser === null || dependentElementValue === null || dependentElementValue.value === null || !filteredDepartments.map((department: Department) => department.departmentId).includes(dependentElementValue.value)) {
                        newValues = newValues.filter((currentValueObject: FilledChecklistValueObject) => currentValueObject.elementId !== dependentElement.elementId);
                        newValues.push(new FilledChecklistValueObject(dependentElement.elementId, '', null, null));
                    }
                }
            });
        }

        const success: boolean = await indexedDBContext.indexedDBService.updateFilledChecklist(new FilledChecklist(props.filledChecklistId, props.checklistId, name, createdDate, newChangedDate, newValues, folderId, props.sortIndex));

        if (!success) {
            setErrorSnackbarMessage('Failed to save changes');
            return;
        }
        
        setValues(newValues);
        
    }, [allUsers, checklistModel?.elements, createdDate, departments, folderId, indexedDBContext.indexedDBService, localStorageContext, name, props.checklistId, props.filledChecklistId, props.sortIndex, userContext, userDepartmentLinks, values]);

    const countryAutocompleteOptions: { label: string, id: string, disabled: boolean }[] = useMemo<{ label: string, id: string, disabled: boolean }[]>(() => {
        
        const sortedCountries: Country[] = [...countries].sort((a: Country, b: Country) => a.name.localeCompare(b.name));

        return sortedCountries.map((country: Country) => {
            return {
                label: country.name,
                id: country.name,
                disabled: false,
            };
        });
    }, [countries]);

    const inspectorAutocompleteOptions: { label: string, id: string, disabled: boolean }[] = useMemo<{ label: string, id: string, disabled: boolean }[]>(() => {

        const filteredInspectors: Inspector[] = inspectors.filter((inspector: Inspector) => inspector.status === '-');
        
        const sortedInspectors: Inspector[] = [...filteredInspectors].sort((a: Inspector, b: Inspector) => a.getNameString().localeCompare(b.getNameString()));

        return sortedInspectors.map((inspector: Inspector) => {
            return {
                label: inspector.getNameString(),
                id: inspector.inspectorId,
                disabled: false,
            };
        });
    }, [inspectors]);

    const inspectingCompanyMenuItems: JSX.Element[] = useMemo<JSX.Element[]>(() => {

        const filteredInspectingCompanies: InspectingCompany[] = inspectingCompanies.filter((inspectingCompany: InspectingCompany) => inspectingCompany.status === '-');

        const sortedInspectingCompanies: InspectingCompany[] = [...filteredInspectingCompanies].sort((a: InspectingCompany, b: InspectingCompany) => a.name.localeCompare(b.name));

        return sortedInspectingCompanies.map((inspectingCompany: InspectingCompany) => {
            return (
                <MenuItem
                    key={inspectingCompany.companyId}
                    value={inspectingCompany.companyId}
                >
                    {inspectingCompany.name}
                </MenuItem>
            );
        });
    }, [inspectingCompanies]);

    const userAutocompleteOptionsMap: Map<string, { label: string, id: string, disabled: boolean }[]> = useMemo<Map<string, { label: string, id: string, disabled: boolean }[]>>(() => {
        
        const sortedUsers: User[] = [...allUsers].sort((a: User, b: User) => a.getNameString().localeCompare(b.getNameString()));

        const newUserAutocompleteOptionsMap: Map<string, { label: string, id: string, disabled: boolean }[]> = new Map<string, { label: string, id: string, disabled: boolean }[]>();

        (checklistModel?.elements ?? []).forEach((element: ChecklistElement) => {

            if (element instanceof UserChecklistElement) {

                const newUserAutocompleteOptions: { label: string, id: string, disabled: boolean }[] = sortedUsers.filter((user: User) => {

                    return element.roleId === null || element.roleId === user.roleId;
                }).map((user: User) => {
                    return {
                        label: user.getNameString(),
                        id: user.userId,
                        disabled: false,
                    };
                });

                newUserAutocompleteOptionsMap.set(element.elementId, newUserAutocompleteOptions);
            }
        });

        return newUserAutocompleteOptionsMap;
    }, [allUsers, checklistModel?.elements]);

    const categoryMenuItemsMap: Map<string, JSX.Element[]> = useMemo<Map<string, JSX.Element[]>>(() => {
        
        const newCategoryMenuItemsMap: Map<string, JSX.Element[]> = new Map<string, JSX.Element[]>();

        const filteredCategories: Category[] = categories.filter((category: Category) => category.status === '-');

        (checklistModel?.elements ?? []).forEach((element: ChecklistElement) => {

            if (element instanceof CategoryChecklistElement) {

                const currentCategories: Category[] = filteredCategories.filter((category: Category) => element.parentCategoryId === category.parentId?.toString());

                const sortedCategories: Category[] = [...currentCategories].sort((a: Category, b: Category) => a.sortNumber - b.sortNumber);

                const newCategoryMenuItems: JSX.Element[] = sortedCategories.map((category: Category) => {
                    return (
                        <MenuItem
                            key={category.categoryId.toString()}
                            value={category.categoryId.toString()}
                        >
                            {category.name}
                        </MenuItem>
                    );
                });

                newCategoryMenuItemsMap.set(element.elementId, newCategoryMenuItems);
            }
        });

        return newCategoryMenuItemsMap;
    }, [categories, checklistModel?.elements]);

    const checklistForm: JSX.Element[] = useMemo<JSX.Element[]>(() => {

        const sortedElements: ChecklistElement[] = [...checklistModel?.elements ?? []].sort((a: ChecklistElement, b: ChecklistElement) => a.sortNumber - b.sortNumber);
        
        return sortedElements.map((element: ChecklistElement) => {
            
            const valueObject: FilledChecklistValueObject | null = values.find((value: FilledChecklistValueObject) => value.elementId === element.elementId) ?? null;

            if (element instanceof SimpleChecklistElement) {

                const currentDateWithoutTime: Date = new Date();
                currentDateWithoutTime.setUTCHours(0, 0, 0, 0);

                return element.buildFormElement(
                    valueObject?.value === '1',
                    valueObject?.comment ?? '',
                    async (value: boolean): Promise<void> => handleValueChange(new FilledChecklistValueObject(element.elementId, value ? '1' : '0', currentDateWithoutTime, valueObject?.comment ?? null), element),
                    async (comment: string): Promise<void> => handleValueChange(new FilledChecklistValueObject(element.elementId, valueObject?.value ?? null, valueObject?.signDate ?? null, comment), element),
                    userContext.currentUser?.username ?? 'ERROR',
                    (valueObject === null || valueObject.signDate === null) ? 'ERROR' : datetimeToVisualString(valueObject.signDate, true, 0),
                    currentTheme.palette.text.primary,
                    windowSize[0],
                );

            } else if (element instanceof CheckboxChecklistElement) {
                return element.buildFormElement(
                    valueObject?.value === '1',
                    async (value: boolean): Promise<void> => handleValueChange(new FilledChecklistValueObject(element.elementId, value ? '1' : '0', null, null), element),
                    currentTheme.palette.text.primary,
                );

            } else if (element instanceof HeaderChecklistElement) {
                return element.buildFormElement(
                    currentTheme.palette.text.primary,
                );

            } else if (element instanceof TextFieldChecklistElement) {
                return element.buildFormElement(
                    valueObject?.value ?? '',
                    async (value: string): Promise<void> => handleValueChange(new FilledChecklistValueObject(element.elementId, value, null, null), element),
                    windowSize[0],
                );

            } else if (element instanceof TextAreaChecklistElement) {
                return element.buildFormElement(
                    valueObject?.value ?? '',
                    async (value: string): Promise<void> => handleValueChange(new FilledChecklistValueObject(element.elementId, value, null, null), element),
                    windowSize[0],
                );

            } else if (element instanceof MaskChecklistElement) {
                return element.buildFormElement(
                    valueObject?.value ?? '',
                    async (value: string): Promise<void> => handleValueChange(new FilledChecklistValueObject(element.elementId, value, null, null), element),
                    windowSize[0],
                );

            } else if (element instanceof DecimalChecklistElement) {
                return element.buildFormElement(
                    valueObject?.value ?? '',
                    async (value: string): Promise<void> => handleValueChange(new FilledChecklistValueObject(element.elementId, value, null, null), element),
                    windowSize[0],
                );

            } else if (element instanceof IntegerChecklistElement) {
                return element.buildFormElement(
                    valueObject?.value ?? '',
                    async (value: string): Promise<void> => handleValueChange(new FilledChecklistValueObject(element.elementId, value, null, null), element),
                    windowSize[0],
                );

            } else if (element instanceof DateChecklistElement) {
                return element.buildFormElement(
                    datetimeStandardParser(valueObject?.value ?? null, 0) ?? null,
                    async (value: Date | null): Promise<void> => handleValueChange(new FilledChecklistValueObject(element.elementId, value === null ? '' : datetimeToApiString(value, 0), null, null), element),
                    windowSize[0],
                );

            } else if (element instanceof CountryChecklistElement) {
                return element.buildFormElement(
                    valueObject?.value ?? '',
                    async (value: string): Promise<void> => handleValueChange(new FilledChecklistValueObject(element.elementId, value, null, null), element),
                    countryAutocompleteOptions,
                    windowSize[0],
                );

            } else if (element instanceof InspectorChecklistElement) {
                return element.buildFormElement(
                    valueObject?.value ?? '',
                    async (value: string): Promise<void> => handleValueChange(new FilledChecklistValueObject(element.elementId, value, null, null), element),
                    inspectorAutocompleteOptions,
                    windowSize[0],
                );

            } else if (element instanceof UserChecklistElement) {
                return element.buildFormElement(
                    valueObject?.value ?? '',
                    async (value: string): Promise<void> => handleValueChange(new FilledChecklistValueObject(element.elementId, value, null, null), element),
                    userAutocompleteOptionsMap.get(element.elementId) ?? [],
                    windowSize[0],
                );

            } else if (element instanceof DropdownChecklistElement) {

                const dependentElement: ChecklistElement | null = sortedElements.find((currentElement: ChecklistElement) => currentElement.elementId === element.dependentElementId) ?? null;
                let dependentValueIndex: number | null = null;

                if (dependentElement instanceof DropdownChecklistElement) {

                    const dependentValue: FilledChecklistValueObject | null = values.find((value: FilledChecklistValueObject) => value.elementId === element.dependentElementId) ?? null;
                    dependentValueIndex = dependentElement.getValueIndexFromValue(dependentValue?.value ?? null);
                }

                return element.buildFormElement(
                    valueObject?.value ?? '',
                    async (value: string): Promise<void> => handleValueChange(new FilledChecklistValueObject(element.elementId, value, null, null), element),
                    dependentValueIndex,
                    windowSize[0],
                );

            } else if (element instanceof InspectingCompanyChecklistElement) {
                return element.buildFormElement(
                    valueObject?.value ?? '',
                    async (value: string): Promise<void> => handleValueChange(new FilledChecklistValueObject(element.elementId, value, null, null), element),
                    inspectingCompanies,
                    inspectingCompanyMenuItems,
                    windowSize[0],
                );

            } else if (element instanceof CategoryChecklistElement) {
                return element.buildFormElement(
                    valueObject?.value ?? '',
                    async (value: string): Promise<void> => handleValueChange(new FilledChecklistValueObject(element.elementId, value, null, null), element),
                    categories.filter((category: Category) => category.status === '-' && element.parentCategoryId === category.parentId?.toString()),
                    categoryMenuItemsMap.get(element.elementId) ?? [],
                    windowSize[0],
                );

            } else if (element instanceof DepartmentChecklistElement) {

                const dependentElement: ChecklistElement | null = sortedElements.find((currentElement: ChecklistElement) => currentElement.elementId === element.dependentElementId) ?? null;
                let dependentUser: User | null = null;

                if (dependentElement instanceof UserChecklistElement) {

                    const dependentValue: FilledChecklistValueObject | null = values.find((value: FilledChecklistValueObject) => value.elementId === element.dependentElementId) ?? null;
                    dependentUser = allUsers.find((user: User) => user.userId === dependentValue?.value) ?? null;
                }

                return element.buildFormElement(
                    valueObject?.value ?? '',
                    async (value: string): Promise<void> => handleValueChange(new FilledChecklistValueObject(element.elementId, value, null, null), element),
                    departments,
                    userDepartmentLinks,
                    dependentUser,
                    windowSize[0],
                );

            } else if (element instanceof LookupChecklistElement) {

                return element.buildFormElement(
                    valueObject?.value ?? '',
                    async (value: string): Promise<void> => handleValueChange(new FilledChecklistValueObject(element.elementId, value, null, null), element),
                    windowSize[0],
                );

            } else {
                return (
                    <div key={element.elementId}>
                        Field not supported yet
                    </div>
                );
            }
        });
    }, [allUsers, categories, categoryMenuItemsMap, checklistModel?.elements, countryAutocompleteOptions, currentTheme.palette.text.primary, departments, handleValueChange, inspectingCompanies, inspectingCompanyMenuItems, inspectorAutocompleteOptions, userAutocompleteOptionsMap, userContext.currentUser?.username, userDepartmentLinks, values, windowSize]);
    
    useEffect(() => {

        function updateSize(): void {
            setWindowSize([window.innerWidth, window.innerHeight]);
        }

        window.addEventListener('resize', updateSize);
        updateSize();
        return () => window.removeEventListener('resize', updateSize);
    }, []);

    useEffect(() => { //fetch data from indexedDB

        let mounted: boolean = true;
        
        async function fetchFromIndexedDB(): Promise<void> {

            const newFilledChecklist: FilledChecklist | null = await indexedDBContext.indexedDBService.getFilledChecklist(props.filledChecklistId);
            const newChecklistModel: ChecklistModel | null = await indexedDBContext.indexedDBService.getChecklistModel(props.checklistId);
            const newCountries: Country[] = await indexedDBContext.indexedDBService.getAllCountries();
            const newAllUsers: User[] = await indexedDBContext.indexedDBService.getAllUsers();
            const newInspectors: Inspector[] = await indexedDBContext.indexedDBService.getAllInspectors();
            const newInspectingCompanies: InspectingCompany[] = await indexedDBContext.indexedDBService.getAllInspectingCompanies();
            const newDepartments: Department[] = await indexedDBContext.indexedDBService.getAllDepartments();
            const newUserDepartmentLinks: UserDepartmentLink[] = await indexedDBContext.indexedDBService.getAllUserDepartmentLinks();
            const newCategories: Category[] = await indexedDBContext.indexedDBService.getAllCategories();

            if (mounted) {
                if (newFilledChecklist === null || newChecklistModel === null) {
                    return;
                }

                setName(newFilledChecklist.name);
                setCreatedDate(newFilledChecklist.createdDate);
                setValues(newFilledChecklist.values);
                setChecklistModel(newChecklistModel);
                setFolderId(newFilledChecklist.folderId);
                setCountries(newCountries);
                setAllUsers(newAllUsers);
                setInspectors(newInspectors);
                setInspectingCompanies(newInspectingCompanies);
                setDepartments(newDepartments);
                setUserDepartmentLinks(newUserDepartmentLinks);
                setCategories(newCategories);

                setDataIsLoaded(true);
            }
        }

        void fetchFromIndexedDB();

        return () => {
            mounted = false;
        };
    }, [indexedDBContext.indexedDBService, props.checklistId, props.filledChecklistId]);

    return (
        <>
            <AppBar 
                position='sticky'
                elevation={0}
                style={{
                    background: currentTheme.palette.background.paper,
                    height: appBarHeight,
                }}
            >
                <Toolbar style={{
                    justifyContent: 'left',
                }}>
                    <IconButton 
                        edge='end' 
                        color='inherit' 
                        onClick={(): void => props.closeSubTab(null)}
                    >
                        <FontAwesomeIcon
                            icon={faArrowLeft}
                            color={currentTheme.palette.text.primary}    
                        />
                    </IconButton>
                    <Typography
                        noWrap
                        style={{
                            marginLeft: '30px',
                            color: currentTheme.palette.text.primary,
                            
                        }}
                    >
                        {'Checklist "' + name + '"'}
                    </Typography>
                </Toolbar>
            </AppBar>
            <div style={{
                textAlign: 'center',
                padding: '25px 5px 5px 5px',
                height: windowSize[1] - navBarHeight - appBarHeight,
                overflow: 'auto',
                position: 'relative',
            }}>
                <div style={{
                    display: 'inline-block',
                    textAlign: 'left',
                    width: Math.min(windowSize[0] * 0.8, 500),
                }}>
                    <form noValidate autoComplete='off' name='checklist'>
                        {checklistForm}
                    </form>
                </div>
                <br />
                <br />

                <ErrorSnackbar 
                    message={errorSnackbarMessage}
                    setMessage={setErrorSnackbarMessage}
                />

                <LoadDataBackdrop
                    dataIsLoaded={dataIsLoaded}
                    zIndex={1000}
                />
            </div>
        </>
    );
}

export default ChecklistItemSubTab;